import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

// Independent of `loadSiteConfig()`'s whole-page cache (lib/siteConfig.js) on purpose: that cache
// is fetched once and shared for the life of the tab, which is right for contact details and GST
// but would mean a customer already on the page when a *scheduled* window begins never finds out.
// This polls fresh instead, but only while something actually needs to know — see `Protected` in
// App.js, which enables it solely for customer/advocate routes (never admin).
const POLL_MS = 20000;

export function useMaintenanceStatus(enabled) {
  const [status, setStatus] = useState(null); // null = not yet known
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const check = () => api.get("/site-config")
      .then(r => { if (alive) setStatus(r.data?.maintenance || { active: false }); })
      .catch(() => {}); // transient failure — keep showing whatever we last knew
    check();
    const id = setInterval(check, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [enabled]);
  return status;
}

/**
 * The admin-side equivalent — polls `GET /admin/maintenance` (richer than the public
 * `/site-config` field: carries `override` too) so `AdminLayout`'s persistent banner stays
 * accurate for as long as an admin is anywhere in the portal, not just on the Disaster Recovery
 * page. Admin is never blocked by a window, so this is purely informational — the point raised
 * was that "Maintenance started now" as a one-off toast is easy to lose track of; a banner that
 * doesn't go away until the window actually ends (or is ended) is what keeps it in view.
 */
export function useAdminMaintenanceWindow() {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(() => api.get("/admin/maintenance")
    .then(r => { setStatus(r.data); return r.data; })
    .catch(() => {}), []);
  useEffect(() => {
    let alive = true;
    const id = setInterval(() => { if (alive) refresh(); }, POLL_MS);
    refresh();
    return () => { alive = false; clearInterval(id); };
  }, [refresh]);
  return [status, refresh];
}
